import React, {useState, useContext, useEffect} from 'react';
import {WaypointsIcon} from 'lucide-react';

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../ui/dialog";
import {Label} from "../../ui/label"
import {Input} from "../../ui/input"
import {Switch} from "../../ui/switch"
import {INITIAL_CANDLES} from "../../../env";
import {ChartContext} from "../../../store/ChartContext";
import {BACKEND_API_URL} from "../../../env";
import TagManager from "react-gtm-module";


function VirtualAssistantPanel({setVirtualAssistantSettings, virtualAssistantSettings, instrument}) {
    /* Main virtual assistant settings panel. */
    const [sensitivity, setSensitivity] = useState(virtualAssistantSettings.sensitivity);
    const [length, setLength] = useState(virtualAssistantSettings.length);
    const [emaPeriod1, setEmaPeriod1] = useState(virtualAssistantSettings.emaPeriod1);
    const [emaPeriod2, setEmaPeriod2] = useState(virtualAssistantSettings.emaPeriod2);
    const [emaPeriod3, setEmaPeriod3] = useState(virtualAssistantSettings.emaPeriod3);
    const [emaPeriod4, setEmaPeriod4] = useState(virtualAssistantSettings.emaPeriod4);
    const [on, setOn] = useState(virtualAssistantSettings.on);
    const [ema, setEma] = useState(virtualAssistantSettings.ema);
    const [emaColor1, setEmaColor1] = useState(virtualAssistantSettings.emaColor1);
    const [emaColor2, setEmaColor2] = useState(virtualAssistantSettings.emaColor2);
    const [emaColor3, setEmaColor3] = useState(virtualAssistantSettings.emaColor3);
    const [emaColor4, setEmaColor4] = useState(virtualAssistantSettings.emaColor4);
    const [channel, setChannel] = useState(virtualAssistantSettings.channel);
    const [channelBackground, setShannelBackground] = useState(virtualAssistantSettings.channelBackground);
    const [channelBars, setChannelBars] = useState(virtualAssistantSettings.channelBars);
    const [channelTimeframe, setChannelTimeframe] = useState(virtualAssistantSettings.channelTimeframe);
    const [channelZone, setChannelZone] = useState(virtualAssistantSettings.channelZone);
    const {granularity} = useContext(ChartContext);
    const [filteredOptions, seFilteredOptions] = useState([granularity]);
    const [notificationTriggerState, setNotificationTriggerState] = useState(
        virtualAssistantSettings.notificationTriggerState || 'open'
    );
    const [isOpen, setIsOpen] = useState(false);

    const handleOpenChange = (isOpen) => {
        TagManager.dataLayer({
            dataLayer: {
                event: 'virtual_assistant_settings_dialog',
                action: isOpen ? 'open' : 'close'
            },
        });
        
        setIsOpen(isOpen);
    };

    useEffect(() => {
        setChannelTimeframe(granularity.value);

        const options = [
            {value: "60", label: "1m"},
            {value: "300", label: "5m"},
            {value: "900", label: "15m"},
            {value: "3600", label: "1h"},
            {value: "7200", label: "2h"},
            {value: "14400", label: "4h"},
            {value: "86400", label: "1d"},
            {value: "604800", label: "1w"},
        ];

        const filtered = options.filter((option, index) => {
            const optionIndex = options.findIndex((opt) => opt.value === granularity.value.toString());
            return (
                index >= optionIndex &&
                index <= optionIndex + 2
            );
        });
        seFilteredOptions(filtered);

    }, [granularity]);

    useEffect(() => {
        // when changed in InstrumentPicker.jsx
        setSensitivity(virtualAssistantSettings.sensitivity);
        setLength(virtualAssistantSettings.length);
    }, [virtualAssistantSettings]);

    const updateVirtualAssistantSettings = () => {
        setVirtualAssistantSettings({
            sensitivity,
            length,
            emaPeriod1,
            emaPeriod2,
            emaPeriod3,
            emaPeriod4,
            on,
            ema,
            emaColor1,
            emaColor2,
            emaColor3,
            emaColor4,
            channel,
            channelBackground,
            channelBars,
            channelTimeframe,
            channelZone,
            notificationTriggerState,
        });
        setIsOpen(false);
    }
    
    const handleChangeTimeframe = async (e) => {
        setChannelTimeframe(e.target.value)
        
        const currency = instrument.display_name
        let timeframe = e.target.value
        
        if(timeframe == 300) {
            timeframe = "5m"
        }

        if(timeframe == 900) {
            timeframe = "15m"
        }

        if(timeframe == 3600) {
            timeframe = "1h"
        }
        
        const ENDPOINT_URL = `${BACKEND_API_URL}/api/currency-timeframe-matches`;
        try {
            const params = new URLSearchParams({
                currency: String(currency),
                timeframe: String(timeframe),
            });
            const url = `${ENDPOINT_URL}?${params.toString()}`;            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: "include"
            });
            
            if (response.status === 200) {
                const data = await response.json();
                console.log('Response data (currency-timeframe-matches):', data);
                
                setSensitivity(data.arrows);
                setLength(data.manual);
            }
            
        } catch (error) {
            console.error('Error sending message (currency-timeframe-matches):', error);
        } finally {
        }
    }

    return <>
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogTrigger>

                <div
                    className="flex items-center space-x-2 focus:ring-2 focus:ring-offset-2 focus:ring-white rounded-md"
                >
                    <WaypointsIcon size={20}/>
                    <span>AI Assistant</span>
                </div>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle>AI Assistant settings</DialogTitle>
                </DialogHeader>
                <div className="flex items-center space-x-2">
                    <Label className="flex flex-row gap-2 items-center justify-center" htmlFor="virtual-assistant">Toggle
                        AI Assistant</Label>
                    <Switch onClick={() => {
                        setOn(!on);
                    }} checked={on} id="virtual-assistant"/>
                    <Label className="flex flex-row gap-2 items-center justify-center"
                           htmlFor="virtual-assistant">EMA</Label>
                    <Switch onClick={() => {
                        setEma(!ema);
                    }} checked={ema} id="virtual-assistant"/>
                </div>
                <div className="flex items-center space-x-2">
                    <Label className="flex flex-row gap-2 items-center justify-center"
                           htmlFor="sensitivity">Arrows</Label>
                    <Input type="number" min="1" max="10" step="1" value={sensitivity} onChange={(e) => {
                        const value = Math.max(1, Math.min(10, Number(e.target.value)));
                        setSensitivity(value);
                    }} id="sensitivity"/>
                </div>
                <div className="flex items-center space-x-2">
                    <Label className="flex flex-row gap-2 items-center justify-center" htmlFor="length">Manual</Label>
                    <Input type="number" min="0" step="1" value={length} onChange={(e) => {
                        setLength(e.target.value);
                    }} id="length"/>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex flex-row gap-2 items-center justify-center">
                        <Label htmlFor="channel-show">Channel
                            Show</Label>
                        <Switch onClick={() => {
                            setChannel(!channel);
                        }} checked={channel} id="channel-show"/>
                    </div>

                    <div className="flex flex-row gap-2 items-center justify-center">
                        <Label htmlFor="channel-background">Channel
                            Background</Label>
                        <Switch onClick={() => {
                            setShannelBackground(!channelBackground);
                        }} checked={channelBackground} id="channel-background"/>
                    </div>

                    <div className="flex flex-row gap-2 items-center justify-center">
                        <Label htmlFor="channel-bars">Channel Bars</Label>
                        <Input type="number" min="0" max={{INITIAL_CANDLES}} style={{width: "70px"}} value={channelBars}
                               onChange={(e) => {
                                   const value = Math.max(0, Math.min(INITIAL_CANDLES, Number(e.target.value)));
                                   setChannelBars(value);
                               }} id="channel-bars"/>
                    </div>

                    <div className="flex flex-row gap-2 items-center justify-center">
                        <Label htmlFor="channel-timeframe">Channel Timeframe</Label>
                        <select id="channel-timeframe" value={channelTimeframe}
                                onChange={(e) => handleChangeTimeframe(e)} className="border rounded-md p-1">
                            {filteredOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="flex items-center space-x-2">
                    <Label className="flex flex-row gap-2 items-center justify-center" htmlFor="channel-zone">
                        Channel - 1st Zone %
                    </Label>
                    <Input type="number" min="0" max="100" step="1" value={channelZone} onChange={(e) => {
                        const value = Math.max(0, Math.min(100, Number(e.target.value)));
                        setChannelZone(value);
                    }} id="channel-zone"/>
                </div>
                <div className="flex items-center space-x-2 mt-4">
                    <Label className="flex flex-row gap-2 items-center justify-center">
                       Show Bar Notifications
                    </Label>
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                            <input
                                type="radio"
                                id="notification-open"
                                name="notification-trigger"
                                value="open"
                                checked={notificationTriggerState === 'open'}
                                onChange={(e) => setNotificationTriggerState(e.target.value)}
                                className="h-4 w-4"
                            />
                            <Label htmlFor="notification-open">Open</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                            <input
                                type="radio"
                                id="notification-close"
                                name="notification-trigger"
                                value="close"
                                checked={notificationTriggerState === 'close'}
                                onChange={(e) => setNotificationTriggerState(e.target.value)}
                                className="h-4 w-4"
                            />
                            <Label htmlFor="notification-close">Close</Label>
                        </div>
                    </div>
                </div>
                {ema && (
                    <div className="flex flex-row gap-2 items-center justify-center flex-wrap">
                        <Label htmlFor="ema-period-1">EMA Period 1</Label>
                        <Input type="number" value={emaPeriod1} onChange={(e) => {
                            setEmaPeriod1(e.target.value);
                        }} id="ema-period-1"/>

                        <Label htmlFor="color-select-1">Select Color</Label>
                        <select id="color-select-1" value={emaColor1} onChange={(e) => setEmaColor1(e.target.value)}
                                className="border rounded-md p-1">
                            <option value="blue">Blue</option>
                            <option value="brown">Brown</option>
                            <option value="green">Green</option>
                            <option value="orange">Orange</option>
                            <option value="pink">Pink</option>
                            <option value="purple">Purple</option>
                            <option value="red">Red</option>
                            <option value="white">White</option>
                            <option value="yellow">Yellow</option>
                        </select>
                    </div>
                )}
                {ema && (
                    <div className="flex flex-row gap-2 items-center justify-center flex-wrap">
                        <Label className="flex flex-row gap-2 items-center justify-center" htmlFor="ema-period-2">EMA
                            Period
                            2</Label>
                        <Input type="number" value={emaPeriod2} onChange={(e) => {
                            setEmaPeriod2(e.target.value);
                        }} id="ema-period-2"/>
                        <Label htmlFor="color-select-2">Select Color</Label>
                        <select id="color-select-2" value={emaColor2} onChange={(e) => setEmaColor2(e.target.value)}
                                className="border rounded-md p-1">
                            <option value="blue">Blue</option>
                            <option value="brown">Brown</option>
                            <option value="green">Green</option>
                            <option value="orange">Orange</option>
                            <option value="pink">Pink</option>
                            <option value="purple">Purple</option>
                            <option value="red">Red</option>
                            <option value="white">White</option>
                            <option value="yellow">Yellow</option>
                        </select>
                    </div>
                )}
                {ema && (
                    <div className="flex flex-row gap-2 items-center justify-center flex-wrap">
                        <Label className="flex flex-row gap-2 items-center justify-center" htmlFor="ema-period-3">EMA
                            Period
                            3</Label>
                        <Input type="number" value={emaPeriod3} onChange={(e) => {
                            setEmaPeriod3(e.target.value);
                        }} id="ema-period-3"/>
                        <Label htmlFor="color-select-3">Select Color</Label>
                        <select id="color-select-3" value={emaColor3} onChange={(e) => setEmaColor3(e.target.value)}
                                className="border rounded-md p-1">
                            <option value="blue">Blue</option>
                            <option value="brown">Brown</option>
                            <option value="green">Green</option>
                            <option value="orange">Orange</option>
                            <option value="pink">Pink</option>
                            <option value="purple">Purple</option>
                            <option value="red">Red</option>
                            <option value="white">White</option>
                            <option value="yellow">Yellow</option>
                        </select>
                    </div>
                )}
                {ema && (
                    <div className="flex flex-row gap-2 items-center justify-center flex-wrap">
                        <Label className="flex flex-row gap-2 items-center justify-center" htmlFor="ema-period-4">EMA
                            Period
                            4</Label>
                        <Input type="number" value={emaPeriod4} onChange={(e) => {
                            setEmaPeriod4(e.target.value);
                        }} id="ema-period-4"/>
                        <Label htmlFor="color-select-4">Select Color</Label>
                        <select id="color-select-4" value={emaColor4} onChange={(e) => setEmaColor4(e.target.value)}
                                className="border rounded-md p-1">
                            <option value="blue">Blue</option>
                            <option value="brown">Brown</option>
                            <option value="green">Green</option>
                            <option value="orange">Orange</option>
                            <option value="pink">Pink</option>
                            <option value="purple">Purple</option>
                            <option value="red">Red</option>
                            <option value="white">White</option>
                            <option value="yellow">Yellow</option>
                        </select>
                    </div>
                 )}
                <div className="flex">
                    <button
                        type="button"
                        className="px-4 py-2 bg-blue-500 text-white rounded-md"
                        onClick={updateVirtualAssistantSettings}
                    >
                        Save
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    </>
        ;
}

export default VirtualAssistantPanel;